import { getToken } from "../utils/auth";
import { API_BASE_URL } from "./URL_Header";

const authHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
});

const triggerBrowserDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Delay revoke so the browser can start the download
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

const parseErrorMessage = async (response, fallbackMessage) => {
  let message = fallbackMessage;
  let missingDesignCodes = [];
  try {
    const data = await response.json();
    message = data.message || message;
    if (Array.isArray(data.missingDesignCodes)) {
      missingDesignCodes = data.missingDesignCodes;
    }
  } catch {
    // ignore parse errors
  }
  const error = new Error(message);
  error.missingDesignCodes = missingDesignCodes;
  throw error;
};

const downloadPdfResponse = async (response, fallbackFileName, fallbackMessage) => {
  if (!response.ok) {
    await parseErrorMessage(response, fallbackMessage);
  }

  const disposition = response.headers.get("Content-Disposition") || "";
  const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i.exec(disposition);
  let fileName = fallbackFileName;
  if (match?.[1]) {
    fileName = match[1].replace(/['"]/g, "").trim();
  }

  // arrayBuffer is more reliable than blob() for large cross-origin PDF responses
  const buffer = await response.arrayBuffer();
  if (!buffer || buffer.byteLength === 0) {
    throw new Error("تم استلام ملف PDF فارغ من السيرفر");
  }

  const blob = new Blob([buffer], { type: "application/pdf" });
  triggerBrowserDownload(blob, fileName);
  return { fileName };
};

/**
 * Generate PDF for the current print batch (server-side) and download it.
 */
export const generateCurrentBatchPdf = async () => {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}/PrintSheet/CurrentBatch`, {
      method: "POST",
      headers: {
        ...authHeaders(),
        Accept: "application/pdf, application/json",
      },
    });
  } catch (err) {
    throw new Error(
      err?.message?.includes("Failed to fetch")
        ? "فشل الاتصال بالسيرفر أثناء تحميل PDF — تأكد أن الـ backend شغال على HTTPS"
        : err?.message || "فشل تحميل PDF"
    );
  }

  return downloadPdfResponse(
    response,
    "print-batch.pdf",
    "Failed to generate current batch PDF"
  );
};

/**
 * Generate PDF for the pending batch currently on screen (by view offset) and download it.
 * Offset 0 is the current printing batch.
 */
export const generateViewedBatchPdf = async (batchOffset = 0) => {
  const offset = Number.isFinite(Number(batchOffset)) ? Number(batchOffset) : 0;
  let response;
  try {
    response = await fetch(
      `${API_BASE_URL}/PrintSheet/ViewedBatch?offset=${encodeURIComponent(offset)}`,
      {
        method: "POST",
        headers: {
          ...authHeaders(),
          Accept: "application/pdf, application/json",
        },
      }
    );
  } catch (err) {
    throw new Error(
      err?.message?.includes("Failed to fetch")
        ? "فشل الاتصال بالسيرفر أثناء تحميل PDF — تأكد أن الـ backend شغال على HTTPS"
        : err?.message || "فشل تحميل PDF"
    );
  }

  return downloadPdfResponse(
    response,
    `print-batch-offset-${offset}.pdf`,
    "Failed to generate viewed batch PDF"
  );
};

/**
 * Generate a custom print-sheet PDF from 12 design-code slots (null = empty).
 * slotFiles: optional map of slotIndex -> File for per-slot design replacements.
 */
export const generateCustomPrintPdf = async (slots, slotFiles = {}) => {
  const form = new FormData();
  form.append("slots", JSON.stringify(slots ?? []));
  Object.entries(slotFiles).forEach(([index, file]) => {
    if (file) form.append(`slotImage_${index}`, file);
  });

  let response;
  try {
    response = await fetch(`${API_BASE_URL}/PrintSheet/Custom`, {
      method: "POST",
      headers: {
        ...authHeaders(),
        Accept: "application/pdf, application/json",
      },
      body: form,
    });
  } catch (err) {
    throw new Error(
      err?.message?.includes("Failed to fetch")
        ? "فشل الاتصال بالسيرفر أثناء تحميل PDF — تأكد أن الـ backend شغال على HTTPS"
        : err?.message || "فشل تحميل PDF"
    );
  }

  return downloadPdfResponse(
    response,
    `custom-print-${Date.now()}.pdf`,
    "Failed to generate custom print PDF"
  );
};
